import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogOut, Server, Ticket, Receipt, ShieldCheck, Activity, LayoutDashboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useLang } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';

export default function Portal() {
  const { t, tv } = useLang();
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!loading && !user) { navigate('/login'); return; }
    if (user) api.get('/portal/overview').then((r) => setData(r.data)).catch(() => {});
  }, [user, loading]);

  if (loading || !user) return <div className="min-h-screen grid place-items-center text-[color:var(--itw-text-2)]">...</div>;

  const kpiIcons = { uptime: Activity, tickets_open: Ticket, services: Server, invoices_due: Receipt };

  return (
    <div className="itw-noise min-h-screen bg-[color:var(--itw-bg)] pt-24 pb-16" data-testid="portal-dashboard-shell">
      <div className="absolute inset-0 itw-grid opacity-20" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[color:var(--itw-cyan)]"><LayoutDashboard className="h-5 w-5" /><span className="text-sm uppercase tracking-widest">{t('portal.title')}</span></div>
            <h1 className="mt-2 font-display text-3xl font-semibold text-[color:var(--itw-text)]">{t('portal.welcome')}, {user.name} 👋</h1>
            <p className="text-sm text-[color:var(--itw-text-3)]">{t('portal.foundation')}</p>
          </div>
          <Button onClick={() => { logout(); navigate('/'); }} data-testid="portal-logout-button"
            className="glass border border-[color:var(--itw-border)] text-[color:var(--itw-text)] hover:border-[color:var(--itw-border-strong)] transition-colors bg-transparent rounded-full">
            <LogOut className="h-4 w-4 mr-2" /> {t('portal.logout')}
          </Button>
        </div>

        {/* KPIs */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {(data?.kpis || []).map((k, i) => {
            const Ic = kpiIcons[k.key] || Activity;
            return (
              <motion.div key={k.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                className="glass rounded-2xl p-5" data-testid="dashboard-kpi-card">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-[color:var(--itw-text-3)]">{tv({ de: k.label_de, en: k.label_en })}</span>
                  <Ic className="h-4 w-4 text-[color:var(--itw-cyan)]" />
                </div>
                <div className="mt-3 font-mono text-2xl font-semibold text-[color:var(--itw-text)]">{k.value}</div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="glass rounded-2xl p-6" data-testid="dashboard-tickets-table">
              <div className="flex items-center gap-2 mb-4"><Ticket className="h-5 w-5 text-[color:var(--itw-cyan)]" /><h3 className="font-display font-semibold text-[color:var(--itw-text)]">{t('portal.tickets')}</h3></div>
              <div className="text-sm text-[color:var(--itw-text-3)] py-8 text-center border border-dashed border-[color:var(--itw-border)] rounded-xl">{t('portal.noTickets')}</div>
            </div>
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4"><Server className="h-5 w-5 text-[color:var(--itw-cyan)]" /><h3 className="font-display font-semibold text-[color:var(--itw-text)]">{t('portal.services')}</h3></div>
              <div className="text-sm text-[color:var(--itw-text-3)] py-8 text-center border border-dashed border-[color:var(--itw-border)] rounded-xl">{t('portal.noServices')}</div>
            </div>
          </div>
          <div className="space-y-6">
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4"><ShieldCheck className="h-5 w-5 text-[color:var(--itw-cyan)]" /><h3 className="font-display font-semibold text-[color:var(--itw-text)]">{t('portal.security')}</h3></div>
              <div className="text-xs text-[color:var(--itw-text-3)] mb-2">{t('portal.securityScore')}</div>
              <Progress value={data?.security?.score || 0} className="h-2" />
              <div className="mt-2 font-mono text-2xl font-semibold text-[color:var(--itw-cyan)]">{data?.security?.score || 0}%</div>
              <div className="mt-4 space-y-2 text-sm">
                {data?.security && Object.entries({ '2FA': data.security.twofa, 'Backups': data.security.backups, 'SSL': data.security.ssl }).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between"><span className="text-[color:var(--itw-text-2)]">{k}</span><span className={v ? 'text-[#2EF2A5]' : 'text-[#FFCC66]'}>{v ? 'OK' : '!'}</span></div>
                ))}
              </div>
            </div>
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4"><Receipt className="h-5 w-5 text-[color:var(--itw-cyan)]" /><h3 className="font-display font-semibold text-[color:var(--itw-text)]">{t('portal.invoices')}</h3></div>
              <div className="text-sm text-[color:var(--itw-text-3)] py-6 text-center border border-dashed border-[color:var(--itw-border)] rounded-xl">{t('portal.noInvoices')}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
